import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from "@angular/router";
import { Store } from "@ngrx/store";
import {
	AbstractTranslatableAlertService,
	CORE_ROUTE,
	CoreState,
	CustomerManagementAction,
	CustomerManagementSelector,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { DevicePostfixPipe } from "@obg/ngx.global";
import { TelemetryService } from "@obg/ngx.global.evo";
import { RouteAction, RouteSelector, RouteService } from "@obg/ngx.router";
import { combineLatest, Observable } from "rxjs";
import { filter, map, take } from "rxjs/operators";

import { FundsProtectionUiInteractionEvent } from "./funds-protection.const";

export const FUNDS_PROTECTION_DIALOG_ID = "FUNDS_PROTECTION_DIALOG_ID";

@Injectable({
	providedIn: "root",
})
export class FundsProtectionGuard implements CanActivate {
	constructor(
		private store: Store<CoreState>,
		private alertService: AbstractTranslatableAlertService,
		private routerService: RouteService,
		private customerAction: CustomerManagementAction,
		private devicePostFixPipe: DevicePostfixPipe,
		private telemetryService: TelemetryService
	) {}

	canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> {
		return combineLatest([
			this.store.pipe(CustomerManagementSelector.hasAcceptedFundsProtection$),
			this.store.select(RouteSelector.getCurrent),
		]).pipe(
			take(1),
			map(([isFundsProtectionAccepted, currentRoute]) => {
				if (isFundsProtectionAccepted) {
					return true;
				}
				this.alertService.open(
					{
						id: FUNDS_PROTECTION_DIALOG_ID,
						title: "uk-funds-protection.title",
						message: this.devicePostFixPipe.transform("uk-funds-protection.message"),
						hideClose: true,
						dialogConfig: {
							disableClose: true,
						},
						mustBeInteracted: true,
						buttons: [
							{
								text: "general.cancel",
							},
							{
								text: "uk-funds-protection.accept",
								handler: () => {
									this.store
										.select(CustomerManagementSelector.hasAcceptedDepositTermsAndConditions)
										.pipe(
											filter((isAccepted) => !!isAccepted),
											take(1)
										)
										// eslint-disable-next-line rxjs/no-ignored-subscription
										.subscribe(() => {
											const routeUrl = this.routerService.generateUrlFromSnapshot(route);
											this.store.dispatch(RouteAction.navigateByUrl(routeUrl));
										});
									this.store.dispatch(this.customerAction.acceptDepositTermsAndConditions());
								},
							},
						],
					},
					[TranslationGroup.General]
				);

				this.telemetryService.track({
					event: FundsProtectionUiInteractionEvent.FundsProtectionOpen,
				});

				if (!currentRoute) {
					this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
				}
				return false;
			})
		);
	}
}
